import { useUserSettings } from "#hooks/use-user-settings"
import i18n from "#lib/i18n"
import { useEffect } from "react"
import { useTranslation as useI18nextTranslation } from "react-i18next"

/**
 * Custom hook wrapper around react-i18next's useTranslation
 * Provides additional utilities for locale management
 * Automatically syncs with user settings if available
 */
export function useI18n(namespace?: string) {
  const { t: tBase, i18n: i18nInstance, ready } = useI18nextTranslation(namespace)
  const { data: userSettings } = useUserSettings()

  // Sync i18n language with user settings when available (only for authenticated users)
  // For unauthenticated users, i18n will use browser language or localStorage
  useEffect(() => {
    if (userSettings?.locale && userSettings.locale !== i18nInstance.language) {
      i18nInstance.changeLanguage(userSettings.locale)
      localStorage.setItem("i18nextLng", userSettings.locale)
    }
  }, [userSettings?.locale, i18nInstance])

  // Wrapper for t that handles namespace automatically from key
  // Supports both "namespace.key" and "namespace:key" formats
  const t = (key: string, options?: any): string => {
    // Check for namespace:key format first (i18next native format)
    if (key.includes(":")) {
      const result = tBase(key, options)
      return typeof result === "string" ? result : key
    }

    // If key contains a dot and starts with a namespace, extract it
    const parts = key.split(".")
    if (parts.length > 1) {
      const possibleNamespace = parts[0]
      // Check if this is a known namespace
      const knownNamespaces = ["common", "pages", "components", "errors", "validation"]
      if (knownNamespaces.includes(possibleNamespace)) {
        // Use the namespace and the rest of the key
        const restOfKey = parts.slice(1).join(".")
        try {
          const result = tBase(restOfKey, { ns: possibleNamespace, ...options })
          // Ensure result is a string
          if (typeof result === "string" && result !== restOfKey && result !== key) {
            return result
          }
          // If translation not found, try without namespace as fallback
          const fallback = tBase(key, options)
          return typeof fallback === "string" && fallback !== key ? fallback : key
        } catch (error) {
          // If error, try fallback
          const fallback = tBase(key, options)
          return typeof fallback === "string" ? fallback : key
        }
      }
    }
    // Fallback: try with default namespace or as-is
    const result = tBase(key, options)
    // Return result if it's a string and different from key, otherwise return key
    return typeof result === "string" && result !== key ? result : key
  }

  const changeLanguage = async (lng: string) => {
    await i18nInstance.changeLanguage(lng)
    localStorage.setItem("i18nextLng", lng)
  }

  const currentLanguage = i18nInstance.language

  return {
    t,
    i18n: i18nInstance,
    ready,
    changeLanguage,
    currentLanguage,
  }
}

/**
 * Re-export the default i18n instance for direct access if needed
 */
export { i18n }
